import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PhoneNumberCard } from "@/components/phone-number-card";
import { AiUsageTab } from "@/components/ai-usage-tab";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CreditCard,
  Receipt,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowUpDown,
  Trash2,
  Star,
  Download,
  ExternalLink,
  XCircle,
  RotateCcw,
  Wifi,
  WifiOff,
  Sparkles,
} from "lucide-react";
import { format, fromUnixTime } from "date-fns";
import { ArrowUp, ArrowDown, Plus, Phone } from "lucide-react";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe as StripeJs } from "@stripe/stripe-js";

let stripePromise: Promise<StripeJs | null> | null = null;
function getStripePromise() {
  if (!stripePromise) {
    const key = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
    stripePromise = key ? loadStripe(key).catch(() => null) : Promise.resolve(null);
  }
  return stripePromise;
}

interface SubscriptionData {
  subscription: {
    id: string;
    status: string;
    currentPeriodStart: number;
    currentPeriodEnd: number;
    cancelAtPeriodEnd: boolean;
    cancelAt: number | null;
  } | null;
  plan: {
    id: string;
    priceId: string;
    amount: number;
    currency: string;
    interval: string;
    intervalCount: number;
    productName: string;
    productDescription: string;
  } | null;
}

interface Invoice {
  id: string;
  number: string | null;
  status: string | null;
  amountDue: number;
  amountPaid: number;
  currency: string;
  created: number;
  periodStart: number;
  periodEnd: number;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  description: string | null;
}

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
  priority: number; // 0 = primary, 1 = fallback 1, 2 = fallback 2, …
}

interface AvailablePlan {
  id: string;
  name: string;
  price: number;
  billingPeriod: string;
  description: string;
  stripePriceId: string | null;
}

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
}

function statusBadge(status: string) {
  const map: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    active: { label: "Active", variant: "default" },
    trialing: { label: "Trial", variant: "secondary" },
    past_due: { label: "Past Due", variant: "destructive" },
    canceled: { label: "Canceled", variant: "destructive" },
    incomplete: { label: "Incomplete", variant: "outline" },
    paid: { label: "Paid", variant: "default" },
    open: { label: "Open", variant: "secondary" },
    void: { label: "Void", variant: "outline" },
    uncollectible: { label: "Uncollectible", variant: "destructive" },
  };
  const config = map[status] ?? { label: status, variant: "outline" as const };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

function brandIcon(brand: string) {
  const icons: Record<string, string> = {
    visa: "💳",
    mastercard: "💳",
    amex: "💳",
    discover: "💳",
  };
  return icons[brand.toLowerCase()] ?? "💳";
}

// ── Current Plan Tab ─────────────────────────────────────────────────────────
function CurrentPlanTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const { data, isLoading } = useQuery<SubscriptionData>({
    queryKey: ["/api/billing/subscription"],
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/billing/subscription/cancel"),
    onSuccess: () => {
      toast({ title: "Subscription canceled", description: "Your plan will remain active until the end of the billing period." });
      queryClient.invalidateQueries({ queryKey: ["/api/billing/subscription"] });
      setCancelDialogOpen(false);
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const reactivateMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/billing/subscription/reactivate"),
    onSuccess: () => {
      toast({ title: "Subscription reactivated", description: "Your subscription will continue after the current period." });
      queryClient.invalidateQueries({ queryKey: ["/api/billing/subscription"] });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { subscription, plan } = data ?? {};

  if (!subscription || !plan) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <CreditCard className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Active Subscription</h3>
          <p className="text-muted-foreground mb-6">You don't have an active paid plan yet.</p>
          <Button onClick={() => window.location.href = "/pricing"}>View Plans</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {subscription.cancelAtPeriodEnd && (
        <Alert className="border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800">
          <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
          <AlertDescription className="text-orange-800 dark:text-orange-200">
            Your subscription is set to cancel on{" "}
            <strong>{format(fromUnixTime(subscription.cancelAt ?? subscription.currentPeriodEnd), "MMMM d, yyyy")}</strong>.
            You'll keep access until then.{" "}
            <Button
              variant="link"
              className="h-auto p-0 text-orange-800 dark:text-orange-200 underline"
              onClick={() => reactivateMutation.mutate()}
              disabled={reactivateMutation.isPending}
            >
              Undo cancellation
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{plan.productName}</CardTitle>
              {plan.productDescription && (
                <CardDescription className="mt-1">{plan.productDescription}</CardDescription>
              )}
            </div>
            {statusBadge(subscription.status)}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Amount</p>
              <p className="font-semibold text-lg">
                {formatCurrency(plan.amount, plan.currency)}
                <span className="text-muted-foreground font-normal text-sm">
                  /{plan.interval}
                </span>
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Current Period</p>
              <p className="font-medium">
                {format(fromUnixTime(subscription.currentPeriodStart), "MMM d")} –{" "}
                {format(fromUnixTime(subscription.currentPeriodEnd), "MMM d, yyyy")}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Next Billing Date</p>
              <p className="font-medium">
                {subscription.cancelAtPeriodEnd
                  ? "No renewal — canceling"
                  : format(fromUnixTime(subscription.currentPeriodEnd), "MMMM d, yyyy")}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Subscription ID</p>
              <p className="font-mono text-xs text-muted-foreground">{subscription.id}</p>
            </div>
          </div>

          <Separator />

          <div className="flex gap-3 flex-wrap">
            {!subscription.cancelAtPeriodEnd && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setCancelDialogOpen(true)}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancel Subscription
              </Button>
            )}
            {subscription.cancelAtPeriodEnd && (
              <Button
                size="sm"
                onClick={() => reactivateMutation.mutate()}
                disabled={reactivateMutation.isPending}
              >
                {reactivateMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4 mr-2" />
                )}
                Reactivate
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription?</DialogTitle>
            <DialogDescription>
              Your subscription will remain active until{" "}
              <strong>{format(fromUnixTime(subscription.currentPeriodEnd), "MMMM d, yyyy")}</strong>.
              After that date your account will downgrade. You can reactivate any time before then.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              Keep Subscription
            </Button>
            <Button
              variant="destructive"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Yes, Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Change Plan Tab ───────────────────────────────────────────────────────────
function ChangePlanTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [confirmPlan, setConfirmPlan] = useState<AvailablePlan | null>(null);

  const { data: subData } = useQuery<SubscriptionData>({
    queryKey: ["/api/billing/subscription"],
  });

  const { data: plans = [], isLoading } = useQuery<AvailablePlan[]>({
    queryKey: ["/api/subscription-plans"],
  });

  const changePlanMutation = useMutation({
    mutationFn: (planId: string) =>
      apiRequest("POST", "/api/billing/subscription/change-plan", { planId }),
    onSuccess: () => {
      toast({ title: "Plan changed", description: "Your subscription has been updated successfully." });
      queryClient.invalidateQueries({ queryKey: ["/api/billing/subscription"] });
      setConfirmPlan(null);
    },
    onError: (err: any) => toast({ title: "Error changing plan", description: err.message, variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const paidPlans = plans.filter((p) => p.stripePriceId && p.price > 0);
  const currentPriceId = subData?.plan?.priceId;

  if (paidPlans.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <ArrowUpDown className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Plans Available</h3>
          <p className="text-muted-foreground">There are no paid plans configured yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Upgrade or downgrade at any time. Charges are prorated so you only pay for what you use.
      </p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {paidPlans.map((plan) => {
          const isCurrent = plan.stripePriceId === currentPriceId;
          return (
            <Card key={plan.id} className={isCurrent ? "border-primary ring-1 ring-primary" : ""}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{plan.name}</CardTitle>
                  {isCurrent && <Badge>Current</Badge>}
                </div>
                {plan.description && (
                  <CardDescription className="text-xs">{plan.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <span className="text-2xl font-bold">${plan.price}</span>
                  <span className="text-muted-foreground text-sm">/{plan.billingPeriod}</span>
                </div>
                <Button
                  className="w-full"
                  variant={isCurrent ? "outline" : "default"}
                  disabled={isCurrent || !subData?.subscription}
                  onClick={() => setConfirmPlan(plan)}
                >
                  {isCurrent ? "Current Plan" : "Switch to This Plan"}
                </Button>
                {!subData?.subscription && !isCurrent && (
                  <p className="text-xs text-muted-foreground text-center">
                    Subscribe first to switch plans
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!confirmPlan} onOpenChange={() => setConfirmPlan(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch to {confirmPlan?.name}?</DialogTitle>
            <DialogDescription>
              You'll be switched to the <strong>{confirmPlan?.name}</strong> plan at{" "}
              <strong>${confirmPlan?.price}/{confirmPlan?.billingPeriod}</strong>. Any unused time on
              your current plan will be prorated and credited automatically.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPlan(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => confirmPlan && changePlanMutation.mutate(confirmPlan.id)}
              disabled={changePlanMutation.isPending}
            >
              {changePlanMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Confirm Switch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Payment Methods Tab ───────────────────────────────────────────────────────
function priorityLabel(index: number) {
  return index === 0 ? "Primary" : `Fallback ${index}`;
}

function AddCardForm({ onDone }: { onDone: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    const { error } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
    });
    setSaving(false);
    if (error) {
      toast({ title: "Card not saved", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Card added", description: "The new card was saved to your account." });
    onDone();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 w-full max-w-xl mx-auto">
      <PaymentElement />
      <Button type="submit" size="lg" className="w-full" disabled={!stripe || saving} data-testid="button-save-card">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
        Save card
      </Button>
    </form>
  );
}

function PaymentMethodsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(null);
  const [stripeInstance, setStripeInstance] = useState<StripeJs | null>(null);

  const { data, isLoading } = useQuery<{ paymentMethods: PaymentMethod[]; defaultId: string | null }>({
    queryKey: ["/api/billing/payment-methods"],
  });

  const reorderMutation = useMutation({
    mutationFn: (order: string[]) =>
      apiRequest("PUT", "/api/billing/payment-methods/priority", { order }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/payment-methods"] });
    },
    onError: (err: any) => {
      toast({ title: "Could not save card order", description: err.message, variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: ["/api/billing/payment-methods"] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/billing/payment-methods/${id}`),
    onSuccess: () => {
      toast({ title: "Card removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/billing/payment-methods"] });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const openAddCard = async () => {
    try {
      setAddOpen(true);
      setSetupClientSecret(null);
      const [stripeJs, res] = await Promise.all([
        getStripePromise(),
        apiRequest("POST", "/api/billing/payment-methods/setup-intent", {}),
      ]);
      const { clientSecret } = await res.json();
      if (!stripeJs) {
        setAddOpen(false);
        toast({ title: "Payments unavailable", description: "Stripe could not be loaded.", variant: "destructive" });
        return;
      }
      setStripeInstance(stripeJs);
      setSetupClientSecret(clientSecret);
    } catch (err: any) {
      setAddOpen(false);
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const methods = data?.paymentMethods ?? [];

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= methods.length) return;
    const order = methods.map((pm) => pm.id);
    [order[index], order[target]] = [order[target], order[index]];
    reorderMutation.mutate(order);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Charges try your <span className="font-medium text-foreground">Primary</span> card first, then each
          fallback in order. Your account is only paused if <span className="font-medium text-foreground">every</span> card fails.
        </p>
        <Button onClick={openAddCard} data-testid="button-add-card">
          <Plus className="w-4 h-4 mr-2" /> Add card
        </Button>
      </div>

      {methods.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CreditCard className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Payment Methods</h3>
            <p className="text-muted-foreground mb-4">
              No saved cards on file. Add your first card — it becomes your primary payment method.
            </p>
            <Button onClick={openAddCard} data-testid="button-add-first-card">
              <Plus className="w-4 h-4 mr-2" /> Add a card
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {methods.map((pm, index) => (
            <Card key={pm.id} data-testid={`row-card-${index}`}>
              <CardContent className="py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => move(index, -1)}
                      disabled={index === 0 || reorderMutation.isPending}
                      title="Move up in charge order"
                      data-testid={`button-card-up-${index}`}
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => move(index, 1)}
                      disabled={index === methods.length - 1 || reorderMutation.isPending}
                      title="Move down in charge order"
                      data-testid={`button-card-down-${index}`}
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="text-2xl">{brandIcon(pm.brand)}</div>
                  <div>
                    <p className="font-medium capitalize">
                      {pm.brand} ending in {pm.last4}
                      <Badge
                        variant={index === 0 ? "default" : "secondary"}
                        className="ml-2 text-xs"
                        data-testid={`badge-priority-${index}`}
                      >
                        {priorityLabel(index)}
                      </Badge>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Expires {pm.expMonth.toString().padStart(2, "0")}/{pm.expYear}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeMutation.mutate(pm.id)}
                  disabled={removeMutation.isPending || (index === 0 && methods.length > 1)}
                  title={index === 0 && methods.length > 1 ? "Promote another card to primary before removing this one" : "Remove card"}
                  data-testid={`button-remove-card-${index}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Cards are processed securely by Stripe and never stored on our servers.
      </p>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="dialog-add-card">
          <div className="flex min-h-full flex-col items-center justify-center gap-6 p-8">
            <DialogHeader className="items-center">
              <DialogTitle className="text-2xl">Add a payment card</DialogTitle>
              <DialogDescription>
                New cards are added to the end of your charge order — you can move them up any time.
              </DialogDescription>
            </DialogHeader>
            {setupClientSecret && stripeInstance ? (
              <Elements stripe={stripeInstance} options={{ clientSecret: setupClientSecret }}>
                <AddCardForm
                  onDone={() => {
                    setAddOpen(false);
                    queryClient.invalidateQueries({ queryKey: ["/api/billing/payment-methods"] });
                  }}
                />
              </Elements>
            ) : (
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Invoices Tab ──────────────────────────────────────────────────────────────
function InvoicesTab() {
  const { data, isLoading } = useQuery<{ invoices: Invoice[] }>({
    queryKey: ["/api/billing/invoices"],
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const invoices = data?.invoices ?? [];

  if (invoices.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Receipt className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Invoices Yet</h3>
          <p className="text-muted-foreground">Invoices will appear here after your first billing cycle.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {invoices.map((inv) => (
        <Card key={inv.id}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="font-mono text-sm font-medium">
                    {inv.number ?? inv.id.slice(0, 12)}
                  </p>
                  {inv.status && statusBadge(inv.status)}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {format(fromUnixTime(inv.created), "MMMM d, yyyy")}
                  {inv.periodStart && inv.periodEnd
                    ? ` · ${format(fromUnixTime(inv.periodStart), "MMM d")} – ${format(fromUnixTime(inv.periodEnd), "MMM d, yyyy")}`
                    : ""}
                </p>
              </div>

              <div className="text-right shrink-0">
                <p className="font-semibold">{formatCurrency(inv.amountPaid || inv.amountDue, inv.currency)}</p>
                <div className="flex gap-2 mt-2 justify-end">
                  {inv.hostedInvoiceUrl && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={inv.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-3 h-3 mr-1" />
                        View
                      </a>
                    </Button>
                  )}
                  {inv.invoicePdf && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={inv.invoicePdf} target="_blank" rel="noopener noreferrer">
                        <Download className="w-3 h-3 mr-1" />
                        PDF
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Connection Status Banner ─────────────────────────────────────────────────
function ConnectionBanner() {
  const { data, isLoading } = useQuery<{ connected: boolean; accountId?: string; error?: string }>({
    queryKey: ["/api/billing/connection-test"],
    retry: false,
  });

  if (isLoading) return null;

  if (data?.connected) {
    return (
      <Alert className="border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800">
        <Wifi className="h-4 w-4 text-green-600 dark:text-green-400" />
        <AlertDescription className="text-green-800 dark:text-green-200">
          Stripe connected · Account <span className="font-mono text-xs">{data.accountId}</span>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert variant="destructive">
      <WifiOff className="h-4 w-4" />
      <AlertDescription>
        Stripe is not connected.{data?.error ? ` Error: ${data.error}` : ""} Billing features are
        unavailable until a valid API key is configured.
      </AlertDescription>
    </Alert>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function BillingPage() {
  const queryClient = useQueryClient();

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/billing/subscription"] });
    queryClient.invalidateQueries({ queryKey: ["/api/billing/invoices"] });
    queryClient.invalidateQueries({ queryKey: ["/api/billing/payment-methods"] });
    queryClient.invalidateQueries({ queryKey: ["/api/billing/connection-test"] });
  };

  return (
    <div className="flex-1 overflow-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Billing & Subscription</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage your plan, payment methods, and view invoices.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <ConnectionBanner />

      <Tabs defaultValue="current-plan">
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="current-plan">
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">Current Plan</span>
            <span className="sm:hidden">Plan</span>
          </TabsTrigger>
          <TabsTrigger value="change-plan">
            <ArrowUpDown className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">Change Plan</span>
            <span className="sm:hidden">Change</span>
          </TabsTrigger>
          <TabsTrigger value="payment-methods">
            <CreditCard className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">Payment</span>
            <span className="sm:hidden">Cards</span>
          </TabsTrigger>
          <TabsTrigger value="phone-number">
            <Phone className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">Phone Number</span>
            <span className="sm:hidden">Phone</span>
          </TabsTrigger>
          <TabsTrigger value="ai-usage" data-testid="tab-ai-usage">
            <Sparkles className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">AI Usage</span>
            <span className="sm:hidden">AI</span>
          </TabsTrigger>
          <TabsTrigger value="invoices">
            <Receipt className="w-4 h-4 mr-1.5" />
            Invoices
          </TabsTrigger>
        </TabsList>

        <TabsContent value="current-plan" className="mt-6">
          <CurrentPlanTab />
        </TabsContent>
        <TabsContent value="change-plan" className="mt-6">
          <ChangePlanTab />
        </TabsContent>
        <TabsContent value="payment-methods" className="mt-6">
          <PaymentMethodsTab />
        </TabsContent>
        <TabsContent value="phone-number" className="mt-6">
          <PhoneNumberCard />
        </TabsContent>
        <TabsContent value="ai-usage" className="mt-6">
          <AiUsageTab />
        </TabsContent>
        <TabsContent value="invoices" className="mt-6">
          <InvoicesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
